import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import Layout from '../components/Layout';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Plus,
  Trash2,
  Edit,
  Power,
  Printer,
  Users as UsersIcon,
} from 'lucide-react';
import { format } from 'date-fns';

interface PrinterUser {
  id: string;
  _id: string;
  username: string;
  role: 'admin' | 'printer';
  isActive: boolean;
  lastLogin?: string | null;
  createdAt: string;
  eventIds?: string[];
}

interface EventLite {
  _id: string;
  id?: string;
  name: string;
  coupleNames?: { bride: string; groom: string };
  eventDate: string;
}

const fetchPrinterUsers = async (): Promise<PrinterUser[]> => {
  const res = await axios.get('/api/users', { params: { role: 'printer' } });
  return res.data.data;
};

const fetchEvents = async (): Promise<EventLite[]> => {
  const res = await axios.get('/api/events');
  return res.data.data;
};

const Users: React.FC = () => {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<PrinterUser | null>(null);

  const { data: users, isLoading } = useQuery<PrinterUser[]>('users-printer', fetchPrinterUsers);
  const { data: events } = useQuery<EventLite[]>('events', fetchEvents);

  const eventLabelById = useMemo(() => {
    const map: Record<string, string> = {};
    (events ?? []).forEach((e) => {
      const id = e._id ?? e.id ?? '';
      if (id) map[id] = e.name;
    });
    return map;
  }, [events]);

  const toggleActive = useMutation(
    ({ id, isActive }: { id: string; isActive: boolean }) =>
      axios.put(`/api/users/${id}`, { isActive }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users-printer');
        toast.success('סטטוס המשתמש עודכן');
      },
      onError: (e: any) => {
        toast.error(e.response?.data?.message || 'שגיאה בעדכון הסטטוס');
      },
    }
  );

  const deleteUser = useMutation((id: string) => axios.delete(`/api/users/${id}`), {
    onSuccess: () => {
      queryClient.invalidateQueries('users-printer');
      toast.success('המשתמש נמחק');
    },
    onError: (e: any) => {
      toast.error(e.response?.data?.message || 'שגיאה במחיקת המשתמש');
    },
  });

  const handleDelete = (user: PrinterUser) => {
    if (window.confirm(`למחוק את המשתמש "${user.username}"?`)) {
      deleteUser.mutate(user._id ?? user.id);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="md:flex md:items-center md:justify-between">
          <div className="min-w-0 flex-1">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl">
              משתמשי מדפסת
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              נהל משתמשים עבור מערכת המדפסת ושייך אותם לאירועים
            </p>
          </div>
          <div className="mt-4 flex md:ml-4 md:mt-0">
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
            >
              <Plus className="h-4 w-4 ml-2" />
              משתמש חדש
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-2/3 mb-3"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : users && users.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {users.map((user) => (
              <UserCard
                key={user._id ?? user.id}
                user={user}
                eventLabelById={eventLabelById}
                onToggle={() =>
                  toggleActive.mutate({ id: user._id ?? user.id, isActive: !user.isActive })
                }
                onEdit={() => setEditingUser(user)}
                onDelete={() => handleDelete(user)}
                toggling={toggleActive.isLoading}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Printer className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">אין משתמשי מדפסת</h3>
            <p className="mt-1 text-sm text-gray-500">צור משתמש מדפסת כדי לאפשר גישה למערכת המדפסת</p>
            <div className="mt-6">
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
              >
                <Plus className="h-4 w-4 ml-2" />
                צור משתמש מדפסת
              </button>
            </div>
          </div>
        )}

        {showCreateModal && (
          <CreatePrinterUserModal
            events={events ?? []}
            onClose={() => setShowCreateModal(false)}
          />
        )}

        {editingUser && (
          <EditPrinterUserModal
            user={editingUser}
            events={events ?? []}
            onClose={() => setEditingUser(null)}
          />
        )}
      </div>
    </Layout>
  );
};

interface UserCardProps {
  user: PrinterUser;
  eventLabelById: Record<string, string>;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  toggling: boolean;
}

const UserCard: React.FC<UserCardProps> = ({ user, eventLabelById, onToggle, onEdit, onDelete, toggling }) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-lg font-semibold text-gray-900">{user.username}</h3>
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              user.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
            }`}
          >
            {user.isActive ? 'פעיל' : 'לא פעיל'}
          </span>
        </div>

        <div className="text-sm text-gray-600 space-y-1">
          <div className="flex items-center gap-2">
            <UsersIcon className="h-4 w-4" />
            <span>
              {user.eventIds && user.eventIds.length > 0
                ? `${user.eventIds.length} אירועים`
                : 'ללא אירועים'}
            </span>
          </div>
          {user.lastLogin && (
            <div className="text-xs text-gray-500">
              התחברות אחרונה: {format(new Date(user.lastLogin), 'dd/MM/yyyy HH:mm')}
            </div>
          )}
        </div>

        {user.eventIds && user.eventIds.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {user.eventIds.map((id) => (
              <span
                key={id}
                className="inline-block bg-blue-50 text-blue-800 text-xs px-2 py-0.5 rounded"
                title={id}
              >
                {eventLabelById[id] ?? id}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-1">
        <button
          onClick={onEdit}
          className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors"
          title="ערוך"
        >
          <Edit className="h-4 w-4" />
        </button>
        <button
          onClick={onToggle}
          disabled={toggling}
          className={`p-2 rounded-md transition-colors ${
            user.isActive
              ? 'text-green-600 hover:text-orange-600 hover:bg-orange-50'
              : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
          } ${toggling ? 'opacity-50 cursor-not-allowed' : ''}`}
          title={user.isActive ? 'השבת' : 'הפעל'}
        >
          <Power className="h-4 w-4" />
        </button>
        <button
          onClick={onDelete}
          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
          title="מחק"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  </div>
);

interface EventPickerProps {
  events: EventLite[];
  selected: string[];
  onChange: (next: string[]) => void;
}

const EventPicker: React.FC<EventPickerProps> = ({ events, selected, onChange }) => {
  const toggle = (id: string) => {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };

  if (events.length === 0) {
    return <p className="text-sm text-gray-500">אין אירועים זמינים</p>;
  }

  return (
    <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100">
      {events.map((e) => {
        const id = e._id ?? e.id ?? '';
        const checked = selected.includes(id);
        return (
          <label
            key={id}
            className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 text-sm"
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={() => toggle(id)}
              className="h-4 w-4 text-blue-600 rounded border-gray-300"
            />
            <span className="flex-1">{e.name}</span>
            <span className="text-xs text-gray-400">
              {format(new Date(e.eventDate), 'dd/MM/yy')}
            </span>
          </label>
        );
      })}
    </div>
  );
};

const CreatePrinterUserModal: React.FC<{ events: EventLite[]; onClose: () => void }> = ({ events, onClose }) => {
  const queryClient = useQueryClient();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [eventIds, setEventIds] = useState<string[]>([]);

  const create = useMutation(
    (data: { username: string; password: string; role: 'printer'; eventIds: string[] }) =>
      axios.post('/api/users', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users-printer');
        toast.success('משתמש מדפסת נוצר');
        onClose();
      },
      onError: (e: any) => {
        toast.error(e.response?.data?.message || 'שגיאה ביצירת המשתמש');
      },
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('סיסמה חייבת להכיל לפחות 6 תווים');
      return;
    }
    create.mutate({ username, password, role: 'printer', eventIds });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">משתמש מדפסת חדש</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">שם משתמש *</label>
              <input
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">סיסמה *</label>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">אירועים</label>
              <EventPicker events={events} selected={eventIds} onChange={setEventIds} />
              <p className="mt-1 text-xs text-gray-500">המשתמש יוכל לראות רק את האירועים המסומנים</p>
            </div>
            <div className="flex gap-4 pt-2">
              <button
                type="submit"
                disabled={create.isLoading}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {create.isLoading ? 'יוצר...' : 'צור משתמש'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300"
              >
                ביטול
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const EditPrinterUserModal: React.FC<{
  user: PrinterUser;
  events: EventLite[];
  onClose: () => void;
}> = ({ user, events, onClose }) => {
  const queryClient = useQueryClient();
  const [password, setPassword] = useState('');
  const [eventIds, setEventIds] = useState<string[]>(user.eventIds ?? []);

  const update = useMutation(
    (data: { password?: string; eventIds?: string[] }) =>
      axios.put(`/api/users/${user._id ?? user.id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users-printer');
        toast.success('המשתמש עודכן');
        onClose();
      },
      onError: (e: any) => {
        toast.error(e.response?.data?.message || 'שגיאה בעדכון המשתמש');
      },
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password && password.length < 6) {
      toast.error('סיסמה חייבת להכיל לפחות 6 תווים');
      return;
    }
    const payload: { password?: string; eventIds?: string[] } = { eventIds };
    if (password) payload.password = password;
    update.mutate(payload);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">עריכת {user.username}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">סיסמה חדשה</label>
              <input
                type="password"
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="השאר ריק כדי לא לשנות"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">אירועים</label>
              <EventPicker events={events} selected={eventIds} onChange={setEventIds} />
            </div>
            <div className="flex gap-4 pt-2">
              <button
                type="submit"
                disabled={update.isLoading}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {update.isLoading ? 'שומר...' : 'שמור'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300"
              >
                ביטול
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Users;
